import { useState, useId, useMemo, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Layers,
  Search,
  CheckCircle2,
  Send,
  Sparkles,
  ArrowRight,
  RotateCcw,
  Eye,
  Tag,
  ShieldCheck,
  X,
  ExternalLink,
  Info,
  Maximize2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import {
  PRODUCTS_CATALOG,
  CATALOG_CATEGORIES,
  CATALOG_CATEGORIES_EN
} from "@/constants/productsCatalog";
import { scrollToId } from "@/lib/scroll";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose
} from "@/components/ui/dialog";

export default function ProductCatalogSection() {
  const { t, i18n } = useTranslation();
  const isEn = Boolean(i18n.language && i18n.language.startsWith("en"));
  const numLocale = isEn ? "en-US" : "es-ES";
  const eur = (v) =>
    new Intl.NumberFormat(numLocale, { style: "currency", currency: "EUR" }).format(v);

  // Reactive translation accessors for catalog data
  const getTitle = (p) => {
    if (!p) return "";
    return isEn && (p.titleEn || p.nameEn) ? (p.titleEn || p.nameEn) : (p.title || p.name);
  };

  const getDesc = (p) => {
    if (!p) return "";
    return isEn && p.descriptionEn ? p.descriptionEn : p.description;
  };

  const getExtDesc = (p) => {
    if (!p) return "";
    return isEn && p.extendedDescriptionEn
      ? p.extendedDescriptionEn
      : (p.extendedDescription || p.description);
  };

  const getCategory = (p) => {
    if (!p) return "";
    return isEn && p.categoryEn ? p.categoryEn : p.category;
  };

  const getSpecs = (p) => {
    if (!p) return [];
    return isEn && p.specsEn?.length ? p.specsEn : (p.specs || []);
  };

  const getCategoryLabel = (cat) => {
    if (!isEn) return cat;
    return CATALOG_CATEGORIES_EN?.[cat] || cat;
  };

  // Catalog filtering state
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");

  // Detailed Product Modal state (Ficha de Producto)
  const [detailedProduct, setDetailedProduct] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Fullscreen Lightbox state (Ampliación Interactiva de Imágenes)
  const [lightbox, setLightbox] = useState({
    isOpen: false,
    product: null,
    imageIndex: 0
  });

  // Currently selected product for quick visual highlight
  const [activeCatalogId, setActiveCatalogId] = useState(PRODUCTS_CATALOG[0]?.id || "");

  // Unique ID for search input
  const searchId = useId();

  // Filtered products list (bilingual reactive search)
  const filteredProducts = useMemo(() => {
    return PRODUCTS_CATALOG.filter((p) => {
      const matchCategory =
        selectedCategory === "Todos" || p.category === selectedCategory;
      const q = searchQuery.toLowerCase().trim();
      const matchSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        (p.nameEn && p.nameEn.toLowerCase().includes(q)) ||
        (p.title && p.title.toLowerCase().includes(q)) ||
        (p.titleEn && p.titleEn.toLowerCase().includes(q)) ||
        p.reference.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        (p.categoryEn && p.categoryEn.toLowerCase().includes(q)) ||
        p.description.toLowerCase().includes(q) ||
        (p.descriptionEn && p.descriptionEn.toLowerCase().includes(q));
      return matchCategory && matchSearch && p.active !== false;
    });
  }, [selectedCategory, searchQuery]);

  // Open detailed modal for a product
  const handleOpenDetails = (product, e) => {
    if (e) e.stopPropagation();
    setDetailedProduct(product);
    setActiveImageIndex(0);
  };

  // Open Interactive Lightbox / Fullscreen Image Viewer
  const handleOpenLightbox = (product, imageIndex = 0, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setLightbox({
      isOpen: true,
      product,
      imageIndex: Math.max(0, imageIndex)
    });
  };

  // Close Lightbox
  const handleCloseLightbox = () => {
    setLightbox((prev) => {
      if (prev.product) {
        setActiveImageIndex(prev.imageIndex);
      }
      return { ...prev, isOpen: false };
    });
  };

  // Lightbox Navigation: Next image
  const handleNextLightboxImage = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!lightbox.product) return;
    const gallery = lightbox.product.images?.length
      ? lightbox.product.images
      : [lightbox.product.image];
    setLightbox((prev) => ({
      ...prev,
      imageIndex: (prev.imageIndex + 1) % gallery.length
    }));
  }, [lightbox.product]);

  // Lightbox Navigation: Previous image
  const handlePrevLightboxImage = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!lightbox.product) return;
    const gallery = lightbox.product.images?.length
      ? lightbox.product.images
      : [lightbox.product.image];
    setLightbox((prev) => ({
      ...prev,
      imageIndex: (prev.imageIndex - 1 + gallery.length) % gallery.length
    }));
  }, [lightbox.product]);

  // Keyboard navigation (Escape, ArrowLeft, ArrowRight) and body scroll lock for Lightbox
  useEffect(() => {
    if (!lightbox.isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCloseLightbox();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        handleNextLightboxImage();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        handlePrevLightboxImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = originalOverflow;
    };
  }, [lightbox.isOpen, handleNextLightboxImage, handlePrevLightboxImage]);

  // Action: "Solicitar presupuesto para este producto"
  const handleRequestQuote = (product, e) => {
    if (e) e.stopPropagation();
    setActiveCatalogId(product.id);
    setDetailedProduct(null); // Close modal if open
    handleCloseLightbox(); // Close lightbox if open

    // 1. Dispatch custom event to sync with QuoteSection
    window.dispatchEvent(
      new CustomEvent("luv:product", { detail: product.id })
    );

    // 2. Smooth scroll to form section
    scrollToId("#presupuesto");

    // 3. User feedback toast
    const productName = getTitle(product);
    toast.success(
      isEn
        ? `Preselected product: ${productName}`
        : `Producto preseleccionado: ${productName}`,
      {
        description: isEn
          ? "We have configured the calculator with the 5% volume discount starting from 100 units."
          : "Hemos preparado el calculador con el 5% de descuento por volumen a partir de 100 u."
      }
    );
  };

  return (
    <section
      id="catalogo"
      data-testid="product-catalog-section"
      className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 relative scroll-mt-20"
    >
      {/* Decorative ambient gradients */}
      <div className="absolute top-1/4 -left-20 w-72 h-72 bg-[#A31D3D]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 -right-20 w-80 h-80 bg-[#F5CB5C]/10 rounded-full blur-3xl pointer-events-none" />

      {/* Section Header */}
      <Reveal className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#F5CB5C] font-semibold bg-[#24020B]/60 border border-[#F5CB5C]/20 px-4 py-1.5 rounded-full">
          <Layers size={14} className="text-[#F5CB5C]" />
          <span>{t("catalogSection.kicker", "Catálogo de Soportes y Artículos de Sumiller")}</span>
        </div>
        <h2 className="mt-4 font-display text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
          {t("catalogSection.titleMain", "¿Qué producto")}{" "}
          <span className="text-gold-gradient">
            {t("catalogSection.titleHighlight", "te interesa?")}
          </span>
        </h2>
        <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed max-w-2xl mx-auto">
          {t(
            "catalogSection.subtitle",
            "Haz clic en cualquier artículo para ver su ficha ampliada con galería de fotos, especificaciones y precios base, o pulsa para solicitar presupuesto directo."
          )}
        </p>
      </Reveal>

      {/* Search and Category Filter Bar */}
      <div className="mt-12 space-y-5">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Real-time search input */}
          <div className="relative w-full md:max-w-md">
            <label htmlFor={searchId} className="sr-only">
              {t("catalogSection.searchPlaceholder", "Buscar artículo o referencia...")}
            </label>
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              id={searchId}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t(
                "catalogSection.searchPlaceholder",
                "Buscar artículo o referencia (ej: sacacorchos, cava, MK-001)..."
              )}
              className="field-dark pl-11 pr-4 py-3 text-sm w-full rounded-2xl border-white/10 bg-[#0E121A]/80 focus:border-[#F5CB5C]/60"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-white px-2 py-1"
                aria-label="Limpiar búsqueda"
              >
                ✕
              </button>
            )}
          </div>

          {/* Catalog count indicator */}
          <div className="text-xs text-gray-400 flex items-center gap-2 self-end md:self-center">
            <span className="inline-block w-2 h-2 rounded-full bg-[#F5CB5C] animate-pulse" />
            <span>
              {filteredProducts.length}{" "}
              {filteredProducts.length === 1
                ? t("catalogSection.availableCount", "artículo disponible")
                : t("catalogSection.availableCountPlural", "artículos disponibles")}
            </span>
          </div>
        </div>

        {/* Categories Pills */}
        <div
          role="tablist"
          aria-label="Categorías de productos"
          className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none"
        >
          {CATALOG_CATEGORIES.map((cat) => {
            const isSelected = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={isSelected}
                onClick={() => setSelectedCategory(cat)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-medium transition-all duration-200 border ${
                  isSelected
                    ? "bg-[#540F20]/70 border-[#F5CB5C]/70 text-[#FAE19C] shadow-[0_0_16px_rgba(245,203,92,0.2)]"
                    : "bg-[#10141D]/60 border-white/10 text-gray-400 hover:text-white hover:border-white/20"
                }`}
              >
                {getCategoryLabel(cat)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Products Grid */}
      <div className="mt-8">
        {filteredProducts.length === 0 ? (
          <div className="glass-card rounded-3xl p-12 text-center text-gray-400 max-w-lg mx-auto">
            <Layers size={40} className="mx-auto text-gray-600 mb-3" />
            <p className="text-base font-medium text-gray-300">
              {t("catalogSection.noResults", "No se encontraron productos con ese criterio de búsqueda.")}
            </p>
            <button
              type="button"
              onClick={() => {
                setSelectedCategory("Todos");
                setSearchQuery("");
              }}
              className="mt-4 inline-flex items-center gap-2 text-sm text-[#F5CB5C] hover:underline"
            >
              <RotateCcw size={14} /> {t("catalogSection.showAllProducts", "Mostrar todos los productos")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
            {filteredProducts.map((p) => {
              const isChosen = activeCatalogId === p.id;
              const title = getTitle(p);
              const desc = getDesc(p);
              const category = getCategory(p);

              return (
                <article
                  key={p.id}
                  data-testid={`catalog-card-${p.id}`}
                  onClick={() => handleOpenDetails(p)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenDetails(p);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-haspopup="dialog"
                  aria-label={`${t("catalogSection.viewCard", "Ver detalles de")} ${title}`}
                  className={`glass-card rounded-3xl overflow-hidden flex flex-col cursor-pointer transition-all duration-300 relative group focus:outline-none focus:ring-2 focus:ring-[#F5CB5C] ${
                    isChosen
                      ? "border-[#F5CB5C] bg-[#24020B]/40 shadow-[0_0_24px_rgba(245,203,92,0.2)]"
                      : "hover:border-[#F5CB5C]/40 hover:-translate-y-1 hover:shadow-xl"
                  }`}
                >
                  {/* Selected Indicator Banner */}
                  {isChosen && (
                    <div className="absolute top-3 right-3 z-20 bg-gradient-to-r from-[#FAE19C] to-[#F5CB5C] text-[#05070A] text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-lg flex items-center gap-1">
                      <CheckCircle2 size={12} className="stroke-[2.5]" />
                      <span>{t("catalogSection.selectedBadge", "Preseleccionado")}</span>
                    </div>
                  )}

                  {/* Reference Badge */}
                  <div className="absolute top-3 left-3 z-20 bg-[#05070A]/85 backdrop-blur-md border border-white/10 text-[10px] tracking-wider font-mono text-[#F5CB5C] px-2.5 py-1 rounded-full">
                    {p.reference}
                  </div>

                  {/* Product Image Container: Clicking opens detailed view (Captura 2) without hover tooltip */}
                  <div
                    className="relative aspect-[4/3] w-full overflow-hidden bg-[#0A0D14]"
                    onClick={(e) => handleOpenDetails(p, e)}
                  >
                    <img
                      src={p.image}
                      alt={title}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/laser-engraving-glass.jpg";
                      }}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0E121A] via-transparent to-transparent pointer-events-none" />
                  </div>

                  {/* Product Details */}
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-[#F5CB5C]/80 font-semibold truncate max-w-[140px] pt-0.5">
                        {category}
                      </span>
                      <div className="text-right shrink-0">
                        {p.isApproximatePrice && (
                          <span className="text-[9px] uppercase tracking-wider font-semibold text-[#F5CB5C] block leading-none mb-1">
                            {t("catalogSection.approxPriceBadge", "Precio aprox.")}
                          </span>
                        )}
                        <span className="font-mono text-xs text-[#FAE19C] font-bold">
                          {p.isApproximatePrice ? "~" : ""}{eur(p.basePrice)}{" "}
                          <span className="text-[10px] text-gray-400 font-normal">
                            /{p.unit === "packs" ? t("catalogSection.packLabel", "packs") : t("catalogSection.unitLabel", "unidades")}
                          </span>
                        </span>
                      </div>
                    </div>

                    <h3 className="mt-1 font-display text-white text-base sm:text-lg font-medium leading-snug line-clamp-1 group-hover:text-[#FAE19C] transition-colors">
                      {title}
                    </h3>

                    <p className="mt-2 text-xs text-gray-400 line-clamp-2 leading-relaxed flex-1">
                      {desc}
                    </p>

                    {/* Action buttons inside card */}
                    <div className="mt-4 pt-3.5 border-t border-white/5 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={(e) => handleOpenDetails(p, e)}
                        className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white transition-colors"
                      >
                        <Info size={13} className="text-[#F5CB5C]" />{" "}
                        {t("catalogSection.viewCard", "Ficha")}
                      </button>

                      <button
                        type="button"
                        onClick={(e) => handleRequestQuote(p, e)}
                        className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-[#05070A] bg-gradient-to-r from-[#FAE19C] to-[#F5CB5C] hover:shadow-[0_0_16px_rgba(245,203,92,0.4)] transition-all"
                      >
                        <span>{t("catalogSection.requestQuote", "Pedir presupuesto")}</span>
                        <ArrowRight size={13} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL DE INFORMACIÓN AMPLIADA DE PRODUCTO (VISTA DETALLADA) */}
      <Dialog
        open={Boolean(detailedProduct)}
        onOpenChange={(open) => {
          if (!open) setDetailedProduct(null);
        }}
      >
        <DialogContent
          data-testid="product-detail-modal"
          className="bg-[#0B0E14] text-gray-200 border-[#F5CB5C]/30 max-w-4xl p-6 sm:p-8 rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => {
            if (lightbox.isOpen) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (lightbox.isOpen) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (lightbox.isOpen) {
              e.preventDefault();
            }
          }}
        >
          {detailedProduct && (
            <div className="relative">
              {/* Close Button Header */}
              <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/10">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[#F5CB5C] bg-[#F5CB5C]/10 border border-[#F5CB5C]/20 px-2.5 py-0.5 rounded-full">
                      {detailedProduct.reference}
                    </span>
                    <span className="text-xs text-gray-400 uppercase tracking-wider">
                      {getCategory(detailedProduct)}
                    </span>
                  </div>
                  <DialogTitle className="font-display text-white text-2xl sm:text-3xl font-bold mt-1.5">
                    {getTitle(detailedProduct)}
                  </DialogTitle>
                </div>
              </div>

              {/* Two Column Layout: Gallery on Left, Details & CTA on Right */}
              <div className="mt-6 grid md:grid-cols-12 gap-6 sm:gap-8 items-start">
                {/* Left Column: Interactive Image Gallery with Lightbox trigger (6 cols) */}
                <div className="md:col-span-6 space-y-3">
                  {/* Main Active Photo (Click opens full-size Lightbox - Captura 3) */}
                  <div
                    className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-[#05070A] border border-white/10 cursor-zoom-in group"
                    onClick={(e) => handleOpenLightbox(detailedProduct, activeImageIndex, e)}
                    role="button"
                    tabIndex={0}
                    aria-label={t("catalogSection.zoomImage", "Ampliar imagen a pantalla completa")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleOpenLightbox(detailedProduct, activeImageIndex, e);
                      }
                    }}
                  >
                    <img
                      src={
                        detailedProduct.images?.[activeImageIndex] ||
                        detailedProduct.image
                      }
                      alt={`${getTitle(detailedProduct)} vista ${activeImageIndex + 1}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/laser-engraving-glass.jpg";
                      }}
                    />
                    <div className="absolute top-2.5 right-2.5 bg-[#05070A]/85 text-[10px] font-mono text-gray-300 px-2.5 py-1 rounded-md backdrop-blur-md border border-white/10 flex items-center gap-1.5 pointer-events-none">
                      <Maximize2 size={11} className="text-[#F5CB5C]" />
                      <span>
                        {t("catalogSection.photoCount", "Foto {{current}} de {{total}}", {
                          current: activeImageIndex + 1,
                          total: detailedProduct.images?.length || 1
                        })}
                      </span>
                    </div>
                    {/* Hover zoom overlay badge */}
                    <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center pointer-events-none">
                      <span className="bg-[#0B0E14]/90 text-white text-xs font-medium px-3.5 py-1.5 rounded-full border border-[#F5CB5C]/40 backdrop-blur-md flex items-center gap-2 shadow-2xl">
                        <Maximize2 size={13} className="text-[#F5CB5C]" />
                        {t("catalogSection.zoomImage", "Ampliar imagen")}
                      </span>
                    </div>
                  </div>

                  {/* Gallery Thumbnails Strip (Click opens full-size Lightbox at that image) */}
                  {detailedProduct.images?.length > 1 && (
                    <div className="flex items-center gap-2.5 overflow-x-auto pb-1">
                      {detailedProduct.images.map((imgUrl, idx) => {
                        const isCurrent = idx === activeImageIndex;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={(e) => {
                              setActiveImageIndex(idx);
                              handleOpenLightbox(detailedProduct, idx, e);
                            }}
                            aria-label={`Ver imagen ampliada ${idx + 1}`}
                            className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all shrink-0 bg-[#05070A] cursor-zoom-in ${
                              isCurrent
                                ? "border-[#F5CB5C] shadow-[0_0_12px_rgba(245,203,92,0.3)] scale-105"
                                : "border-white/10 opacity-60 hover:opacity-100 hover:border-white/40"
                            }`}
                          >
                            <img
                              src={imgUrl}
                              alt={`Miniatura ${idx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/assets/laser-engraving-glass.jpg";
                              }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Right Column: Information, Pricing, Specs and CTA (6 cols) */}
                <div className="md:col-span-6 space-y-5">
                  {/* Pricing Box with 5% Volume Rule */}
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-[#24020B]/60 to-[#121620]/80 border border-[#F5CB5C]/25 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 block font-semibold">
                        {detailedProduct.isApproximatePrice
                          ? t("catalogSection.approxPriceTitle", "Precio aproximado")
                          : t("catalogSection.approxBaseRate", "Tarifa base orientativa")}
                      </span>
                      {detailedProduct.isApproximatePrice && (
                        <span className="text-[10px] font-mono text-[#05070A] bg-gradient-to-r from-[#FAE19C] to-[#F5CB5C] px-2.5 py-0.5 rounded-full font-bold shadow-sm">
                          {t("catalogSection.approxBadgeFull", "Precio aproximado")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-3xl font-bold text-gold-gradient">
                        {detailedProduct.isApproximatePrice ? "~" : ""}{eur(detailedProduct.basePrice)}
                      </span>
                      <span className="text-xs text-gray-400">
                        / {detailedProduct.unit === "packs" ? t("catalogSection.packLabel", "packs") : t("catalogSection.unitLabel", "unidades")} (+ IVA)
                      </span>
                    </div>

                    {detailedProduct.isApproximatePrice && (
                      <p className="text-xs text-amber-200/90 bg-[#F5CB5C]/10 border border-[#F5CB5C]/20 p-2.5 rounded-xl leading-relaxed">
                        {isEn && detailedProduct.priceNoteDetailEn
                          ? detailedProduct.priceNoteDetailEn
                          : (detailedProduct.priceNoteDetail || t("catalogSection.approxGeneralNote", "El precio indicado es orientativo y aproximado según modelo, acabado y tirada."))}
                      </p>
                    )}

                    <div className="pt-2 border-t border-white/10 flex items-center gap-2 text-xs text-emerald-300">
                      <Sparkles size={14} className="text-[#F5CB5C]" />
                      <span>
                        <strong>−5%</strong> {t("catalogSection.directDiscount50", "de descuento directo a partir de 100 unidades.")}
                      </span>
                    </div>
                  </div>

                  {/* Extended Description */}
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1.5">
                      {t("catalogSection.productDescriptionTitle", "Descripción del artículo")}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                      {getExtDesc(detailedProduct)}
                    </p>
                  </div>

                  {/* Technical Specifications */}
                  {getSpecs(detailedProduct).length > 0 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2">
                        {t("catalogSection.technicalSpecsTitle", "Especificaciones técnicas")}
                      </h4>
                      <ul className="space-y-1.5 text-xs text-gray-300">
                        {getSpecs(detailedProduct).map((spec, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 size={14} className="text-[#F5CB5C] mt-0.5 shrink-0" />
                            <span>{spec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Primary CTA: "Solicitar presupuesto para este producto" */}
                  <div className="pt-4 border-t border-white/10 space-y-2">
                    <button
                      type="button"
                      data-testid="modal-btn-request-quote"
                      onClick={() => handleRequestQuote(detailedProduct)}
                      className="w-full inline-flex items-center justify-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-bold text-[#05070A] bg-gradient-to-br from-[#FAE19C] via-[#F5CB5C] to-[#C69222] hover:shadow-[0_0_28px_rgba(245,203,92,0.45)] hover:-translate-y-0.5 transition-all duration-300"
                    >
                      <Send size={16} />
                      <span>{t("catalogSection.requestQuoteForProduct", "Solicitar presupuesto para este producto")}</span>
                    </button>
                    <p className="text-[11px] text-center text-gray-400">
                      {t("catalogSection.preselectHint", "Preseleccionará este producto en el calculador con el 5% de descuento automático a partir de 100 u.")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MODAL FLOTANTE / LIGHTBOX DE IMAGEN A TAMAÑO COMPLETO (PORTAL EN PRIMER PLANO) */}
      {lightbox.isOpen &&
        lightbox.product &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("catalogSection.zoomImage", "Vista ampliada de la imagen")}
            data-testid="image-lightbox-modal"
            onClick={handleCloseLightbox}
            className="fixed inset-0 z-[99999] pointer-events-auto flex flex-col items-center justify-between p-3 sm:p-6 bg-black/95 backdrop-blur-2xl animate-in fade-in duration-200 select-none overflow-hidden"
            style={{ pointerEvents: "auto" }}
          >
          {/* Top Bar: Title, Reference, Counter and Close Button */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl flex items-center justify-between gap-4 py-2.5 px-4 bg-[#0B0E14]/85 backdrop-blur-md rounded-2xl border border-white/10 z-10"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="font-mono text-[11px] text-[#F5CB5C] bg-[#F5CB5C]/10 border border-[#F5CB5C]/25 px-2.5 py-0.5 rounded-full shrink-0">
                {lightbox.product.reference}
              </span>
              <h3 className="text-white text-sm sm:text-base font-medium truncate">
                {getTitle(lightbox.product)}
              </h3>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              {/* Photo Counter */}
              <span className="text-xs text-gray-400 font-mono hidden sm:inline-block">
                {t("catalogSection.photoCount", "Foto {{current}} de {{total}}", {
                  current: lightbox.imageIndex + 1,
                  total: lightbox.product.images?.length || 1
                })}
              </span>

              {/* Close Button ("X") */}
              <button
                type="button"
                data-testid="lightbox-close-button"
                onClick={handleCloseLightbox}
                aria-label={t("catalogSection.closeLightbox", "Cerrar imagen ampliada (Esc)")}
                className="p-2 rounded-full bg-white/10 hover:bg-[#F5CB5C] hover:text-[#05070A] text-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#F5CB5C]"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Central Image Viewport with Nav Arrows */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative flex-1 w-full max-w-5xl flex items-center justify-center my-3 sm:my-4"
          >
            {/* Previous Arrow */}
            {(lightbox.product.images?.length || 0) > 1 && (
              <button
                type="button"
                data-testid="lightbox-prev-button"
                onClick={handlePrevLightboxImage}
                aria-label={t("catalogSection.prevImage", "Imagen anterior")}
                className="absolute left-2 sm:left-4 z-20 p-2.5 sm:p-3 rounded-full bg-[#05070A]/80 hover:bg-[#F5CB5C] hover:text-[#05070A] text-white border border-white/20 transition-all duration-200 backdrop-blur-md shadow-2xl focus:outline-none focus:ring-2 focus:ring-[#F5CB5C]"
              >
                <ChevronLeft size={22} />
              </button>
            )}

            {/* High-Resolution Full-Size Image */}
            <div className="relative max-h-[72vh] max-w-full flex items-center justify-center p-2">
              <img
                src={
                  lightbox.product.images?.[lightbox.imageIndex] ||
                  lightbox.product.image
                }
                alt={`${getTitle(lightbox.product)} - vista ${lightbox.imageIndex + 1}`}
                className="max-h-[70vh] sm:max-h-[74vh] max-w-full object-contain rounded-2xl shadow-2xl border border-white/10 ring-1 ring-white/5 transition-all duration-300"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/assets/laser-engraving-glass.jpg";
                }}
              />
            </div>

            {/* Next Arrow */}
            {(lightbox.product.images?.length || 0) > 1 && (
              <button
                type="button"
                data-testid="lightbox-next-button"
                onClick={handleNextLightboxImage}
                aria-label={t("catalogSection.nextImage", "Siguiente imagen")}
                className="absolute right-2 sm:right-4 z-20 p-2.5 sm:p-3 rounded-full bg-[#05070A]/80 hover:bg-[#F5CB5C] hover:text-[#05070A] text-white border border-white/20 transition-all duration-200 backdrop-blur-md shadow-2xl focus:outline-none focus:ring-2 focus:ring-[#F5CB5C]"
              >
                <ChevronRight size={22} />
              </button>
            )}
          </div>

          {/* Bottom Bar: Thumbnails Strip + Quick CTA */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-3 p-3 bg-[#0B0E14]/85 backdrop-blur-md rounded-2xl border border-white/10 z-10"
          >
            {/* Gallery Thumbnails */}
            {(lightbox.product.images?.length || 0) > 1 ? (
              <div className="flex items-center gap-2 overflow-x-auto max-w-full pb-1 scrollbar-none">
                {lightbox.product.images.map((imgUrl, idx) => {
                  const isCur = idx === lightbox.imageIndex;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() =>
                        setLightbox((prev) => ({ ...prev, imageIndex: idx }))
                      }
                      aria-label={`Ver foto ${idx + 1}`}
                      className={`w-12 h-12 rounded-xl overflow-hidden border-2 transition-all shrink-0 bg-[#05070A] ${
                        isCur
                          ? "border-[#F5CB5C] scale-105 shadow-[0_0_12px_rgba(245,203,92,0.4)]"
                          : "border-white/10 opacity-50 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={imgUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-xs text-gray-400">
                {getCategory(lightbox.product)} · {lightbox.product.isApproximatePrice ? `~${eur(lightbox.product.basePrice)} (${t("catalogSection.approxPriceBadge", "Precio aprox.")})` : eur(lightbox.product.basePrice)}
              </div>
            )}

            {/* Quick Action CTAs */}
            <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
              <button
                type="button"
                onClick={() => {
                  const p = lightbox.product;
                  handleCloseLightbox();
                  handleOpenDetails(p);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-300 hover:text-white border border-white/15 rounded-full hover:border-white/30 transition-colors"
              >
                <Info size={13} className="text-[#F5CB5C]" />
                <span>{t("catalogSection.viewCard", "Ficha técnica")}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  const p = lightbox.product;
                  handleCloseLightbox();
                  handleRequestQuote(p);
                }}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold text-[#05070A] bg-gradient-to-r from-[#FAE19C] to-[#F5CB5C] hover:shadow-[0_0_18px_rgba(245,203,92,0.4)] transition-all"
              >
                <Send size={13} />
                <span>{t("catalogSection.requestQuote", "Pedir presupuesto")}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
